<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import BookingsEditor from "./BookingsEditor.vue";
import ArchiveEditor from "./ArchiveEditor.vue";
import CurrentlyBuildingEditor from "./CurrentlyBuildingEditor.vue";
import ExperimentsEditor from "./ExperimentsEditor.vue";
import MenuEditor from "./MenuEditor.vue";
import ProjectsEditor from "./ProjectsEditor.vue";
import ServicesEditor from "./ServicesEditor.vue";
import ServiceAreaEditor from "./ServiceAreaEditor.vue";
import GalleryEditor from "./GalleryEditor.vue";
import ContactFormEditor from "./ContactFormEditor.vue";

import {
  api,
  type ContentBlock,
  type PageDetail,
  type PageInput,
  type PageSummary,
  type PublishedRelease,
  type SiteSummary,
} from "./api";

const props = defineProps<{ site: SiteSummary }>();
const emit = defineEmits<{ back: [] }>();

interface EditorPage extends PageInput {
  id: string | null;
}

const pages = ref<PageSummary[]>([]);
const draft = ref<EditorPage | null>(null);
const currentRelease = ref<PublishedRelease | null>(null);
const canEdit = ref(false);
const loading = ref(true);
const loadingPage = ref(false);
const saving = ref(false);
const publishing = ref(false);
const error = ref("");
const notice = ref("");
const selectedPlugin = ref<string | null>(null);

const previewUrl = "/api/sites/" + props.site.id + "/preview/";
const canManageContentPages = computed(() => props.site.role === "platform");
const enabledPlugins = computed(() =>
  props.site.availablePlugins.filter((plugin) => props.site.modules.includes(plugin.key)),
);

function emptyBlock(): ContentBlock {
  return {
    type: "section",
    heading: "",
    text: "",
    image_url: "",
    alt: "",
  };
}

function nextPagePath() {
  if (!pages.value.some((page) => page.path === "/")) {
    return "/";
  }
  let number = 1;
  while (pages.value.some((page) => page.path === "/new-page-" + number)) {
    number += 1;
  }
  return "/new-page-" + number;
}

function newPage() {
  error.value = "";
  notice.value = "";
  selectedPlugin.value = null;
  draft.value = {
    id: null,
    title: "Untitled page",
    path: nextPagePath(),
    body: [emptyBlock()],
    seo: { title: "", description: "" },
  };
}

function asEditorPage(page: PageDetail): EditorPage {
  return {
    id: page.id,
    title: page.title,
    path: page.path,
    body: page.body.map((block) => ({ ...block })),
    seo: {
      title: page.seo.title ?? "",
      description: page.seo.description ?? "",
    },
  };
}

async function loadPages(selectFirst = true) {
  const response = await api.pages(props.site.id);
  pages.value = response.pages;
  currentRelease.value = response.currentRelease;
  canEdit.value = response.canEdit;
  if (selectFirst && response.pages[0]) {
    await selectPage(response.pages[0].id);
  } else if (selectFirst) {
    newPage();
  }
}

async function selectPage(pageId: string) {
  error.value = "";
  notice.value = "";
  selectedPlugin.value = null;
  loadingPage.value = true;
  try {
    draft.value = asEditorPage(await api.page(props.site.id, pageId));
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the page";
  } finally {
    loadingPage.value = false;
  }
}

function selectPlugin(pluginKey: string) {
  error.value = "";
  notice.value = "";
  selectedPlugin.value = pluginKey;
}

function pageInput(): PageInput {
  if (!draft.value) {
    throw new Error("No page is selected");
  }
  return {
    title: draft.value.title,
    path: draft.value.path,
    body: draft.value.body,
    seo: draft.value.seo,
  };
}

async function savePage() {
  if (!draft.value || !canEdit.value) {
    return;
  }
  saving.value = true;
  error.value = "";
  notice.value = "";
  try {
    const saved = draft.value.id
      ? await api.updatePage(props.site.id, draft.value.id, pageInput())
      : await api.createPage(props.site.id, pageInput());
    draft.value = asEditorPage(saved);
    await loadPages(false);
    notice.value = "Draft saved. Publish the website when you are ready.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to save the page";
  } finally {
    saving.value = false;
  }
}

async function publishWebsite() {
  if (!canEdit.value || pages.value.length === 0) {
    return;
  }
  publishing.value = true;
  error.value = "";
  notice.value = "";
  try {
    const response = await api.publishSite(props.site.id);
    currentRelease.value = response.release;
    notice.value = "Release " + response.release.number + " is now live.";
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to publish the website";
  } finally {
    publishing.value = false;
  }
}

function addSection() {
  draft.value?.body.push(emptyBlock());
}

function removeSection(index: number) {
  draft.value?.body.splice(index, 1);
}

function moveSection(index: number, direction: -1 | 1) {
  if (!draft.value) {
    return;
  }
  const target = index + direction;
  if (target < 0 || target >= draft.value.body.length) {
    return;
  }
  const [block] = draft.value.body.splice(index, 1);
  if (block) {
    draft.value.body.splice(target, 0, block);
  }
}

onMounted(async () => {
  try {
    if (canManageContentPages.value) {
      await loadPages();
    } else {
      selectedPlugin.value = enabledPlugins.value[0]?.key ?? null;
    }
  } catch (caught) {
    error.value = caught instanceof Error ? caught.message : "Unable to load the website";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="editor-shell">
    <header class="editor-topbar">
      <div class="site-heading">
        <button class="icon-button" type="button" aria-label="Back to websites" @click="emit('back')">
          ←
        </button>
        <div>
          <p class="editor-eyebrow">{{ site.tenantName }}</p>
          <h1>{{ site.name }}</h1>
        </div>
      </div>
      <div class="topbar-actions">
        <template v-if="canManageContentPages">
          <span v-if="currentRelease" class="release">
            Live release {{ currentRelease.number }}
          </span>
          <span v-else class="release draft-release">Not published</span>
        </template>
        <a class="secondary-button" :href="previewUrl" target="_blank" rel="noopener">
          Preview website
        </a>
        <button
          v-if="canManageContentPages"
          type="button"
          :disabled="publishing || !canEdit || pages.length === 0"
          @click="publishWebsite"
        >
          {{ publishing ? "Publishing…" : "Publish website" }}
        </button>
      </div>
    </header>

    <div v-if="loading" class="editor-loading">Loading website…</div>
    <div v-else class="editor-layout">
      <aside class="page-sidebar">
        <template v-if="canManageContentPages">
          <div class="sidebar-heading">
            <div>
              <p class="editor-eyebrow">Content</p>
              <h2>Pages</h2>
            </div>
            <button
              class="add-page"
              type="button"
              :disabled="!canEdit"
              aria-label="Create page"
              @click="newPage"
            >
              +
            </button>
          </div>
          <nav aria-label="Website pages">
            <button
              v-for="page in pages"
              :key="page.id"
              class="page-link"
              :class="{ active: !selectedPlugin && draft?.id === page.id }"
              type="button"
              @click="selectPage(page.id)"
            >
              <span>{{ page.title }}</span>
              <small>{{ page.path }}</small>
            </button>
          </nav>
          <p v-if="!pages.length" class="sidebar-empty">Create your first page to get started.</p>
        </template>

        <template v-if="enabledPlugins.length">
          <div class="sidebar-section-heading" :class="{ 'plugins-only': !canManageContentPages }">
            <p class="editor-eyebrow">Site features</p>
            <h2>Plugins</h2>
          </div>
          <nav aria-label="Website plugins">
            <button
              v-for="plugin in enabledPlugins"
              :key="plugin.key"
              class="page-link plugin-link"
              :class="{ active: selectedPlugin === plugin.key }"
              type="button"
              @click="selectPlugin(plugin.key)"
            >
              <span>{{ plugin.name }}</span>
              <small>Manage plugin</small>
            </button>
          </nav>
        </template>
      </aside>

      <main class="page-workspace" :class="{ 'plugin-workspace': selectedPlugin }">
        <template v-if="selectedPlugin">
          <MenuEditor v-if="selectedPlugin === 'menu'" :site-id="site.id" />
          <BookingsEditor v-else-if="selectedPlugin === 'table-bookings' || selectedPlugin === 'appointments'" :site-id="site.id" />
          <ProjectsEditor v-else-if="selectedPlugin === 'projects'" :site-id="site.id" />
          <ServicesEditor v-else-if="selectedPlugin === 'services'" :site-id="site.id" />
          <ServiceAreaEditor v-else-if="selectedPlugin === 'service-area'" :site-id="site.id" />
          <GalleryEditor v-else-if="selectedPlugin === 'gallery'" :site-id="site.id" />
          <ContactFormEditor v-else-if="selectedPlugin === 'contact-form'" :site-id="site.id" />
          <CurrentlyBuildingEditor v-else-if="selectedPlugin === 'currently-building'" :site-id="site.id" />
          <ExperimentsEditor v-else-if="selectedPlugin === 'experiments'" :site-id="site.id" />
          <ArchiveEditor v-else-if="selectedPlugin === 'archive'" :site-id="site.id" />
          <section v-else class="form-card">
            <div>
              <p class="editor-eyebrow">Enabled plugin</p>
              <h2>{{ enabledPlugins.find(plugin => plugin.key === selectedPlugin)?.name }}</h2>
            </div>
            <p class="sidebar-empty">This plugin does not have a manager screen yet.</p>
          </section>
        </template>

        <template v-else-if="canManageContentPages">
          <p v-if="error" class="editor-message error-message" role="alert">{{ error }}</p>
          <p v-if="notice" class="editor-message success-message" role="status">{{ notice }}</p>
          <p v-if="!canEdit" class="editor-message info-message">
            You have read-only access to this website.
          </p>

          <div v-if="loadingPage" class="editor-loading">Loading page…</div>
          <form v-else-if="draft" class="page-form" @submit.prevent="savePage">
          <section class="form-card page-details">
            <div class="section-heading">
              <div>
                <p class="editor-eyebrow">Page details</p>
                <h2>{{ draft.id ? draft.title : "New page" }}</h2>
              </div>
              <button type="submit" :disabled="saving || !canEdit">
                {{ saving ? "Saving…" : "Save draft" }}
              </button>
            </div>

            <div class="two-columns">
              <label>
                Page title
                <input v-model="draft.title" :disabled="!canEdit" maxlength="200" required>
              </label>
              <label>
                URL path
                <input
                  v-model="draft.path"
                  :disabled="!canEdit"
                  maxlength="240"
                  placeholder="/about"
                  required
                >
              </label>
            </div>
          </section>

          <section class="form-card">
            <div class="section-heading">
              <div>
                <p class="editor-eyebrow">Page content</p>
                <h2>Sections</h2>
              </div>
              <button class="secondary-button" type="button" :disabled="!canEdit" @click="addSection">
                Add section
              </button>
            </div>

            <div v-if="draft.body.length" class="sections">
              <article v-for="(block, index) in draft.body" :key="index" class="content-section">
                <div class="content-section-heading">
                  <span>Section {{ index + 1 }}</span>
                  <div>
                    <button
                      class="mini-button"
                      type="button"
                      :disabled="!canEdit || index === 0"
                      aria-label="Move section up"
                      @click="moveSection(index, -1)"
                    >
                      ↑
                    </button>
                    <button
                      class="mini-button"
                      type="button"
                      :disabled="!canEdit || index === draft.body.length - 1"
                      aria-label="Move section down"
                      @click="moveSection(index, 1)"
                    >
                      ↓
                    </button>
                    <button
                      class="mini-button danger"
                      type="button"
                      :disabled="!canEdit"
                      aria-label="Remove section"
                      @click="removeSection(index)"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                <label>
                  Heading
                  <input v-model="block.heading" :disabled="!canEdit" maxlength="200">
                </label>
                <label>
                  Text
                  <textarea v-model="block.text" :disabled="!canEdit" rows="6" maxlength="10000" />
                </label>
              </article>
            </div>
            <div v-else class="empty-sections">
              <p>This page has no content sections yet.</p>
              <button type="button" :disabled="!canEdit" @click="addSection">Add the first section</button>
            </div>
          </section>

          <section class="form-card">
            <div class="section-heading">
              <div>
                <p class="editor-eyebrow">Search engines</p>
                <h2>Basic SEO</h2>
              </div>
            </div>
            <label>
              SEO title
              <input
                v-model="draft.seo.title"
                :disabled="!canEdit"
                maxlength="200"
                :placeholder="draft.title"
              >
            </label>
            <label>
              Meta description
              <textarea
                v-model="draft.seo.description"
                :disabled="!canEdit"
                rows="3"
                maxlength="320"
                placeholder="A short description for search results"
              />
            </label>
          </section>

          <div class="form-footer">
            <span>Saving creates a draft revision. Visitors see it after you publish.</span>
            <button type="submit" :disabled="saving || !canEdit">
              {{ saving ? "Saving…" : "Save draft" }}
            </button>
          </div>
          </form>
        </template>
        <section v-else class="form-card">
          <div>
            <p class="editor-eyebrow">Site features</p>
            <h2>No editable features</h2>
          </div>
          <p class="sidebar-empty">
            Your website does not currently have any customer-editable plugins enabled.
          </p>
        </section>
      </main>
    </div>
  </div>
</template>

<style scoped>
.editor-shell { min-height: 100vh; background: #f4f6f2; }
.editor-topbar {
  display: flex;
  position: sticky;
  z-index: 10;
  top: 0;
  min-height: 5.5rem;
  padding: 1rem 2rem;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #dce2dc;
  background: rgb(255 255 255 / 94%);
  backdrop-filter: blur(12px);
}
.site-heading, .topbar-actions, .section-heading, .content-section-heading, .form-footer {
  display: flex;
  align-items: center;
}
.site-heading { gap: 1rem; }
.site-heading h1 { margin: 0; font-size: 1.45rem; letter-spacing: -0.02em; }
.editor-eyebrow {
  margin: 0 0 0.2rem;
  color: #6b7b72;
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.icon-button, .add-page, .mini-button {
  display: inline-grid;
  padding: 0;
  place-items: center;
  color: #334b40;
  background: #edf1ed;
}
.icon-button { width: 2.5rem; height: 2.5rem; font-size: 1.2rem; }
.topbar-actions { gap: 0.75rem; }
.release {
  padding: 0.4rem 0.7rem;
  border-radius: 999px;
  color: #316146;
  background: #e4f1e8;
  font-size: 0.75rem;
  font-weight: 800;
}
.draft-release { color: #8a5b25; background: #f8ecd9; }
.secondary-button {
  display: inline-flex;
  border: 1px solid #c8d2cb;
  color: #29483a;
  background: white;
  text-decoration: none;
}
.editor-layout { display: grid; min-height: calc(100vh - 5.5rem); grid-template-columns: 17rem 1fr; }
.page-sidebar {
  display: block;
  position: sticky;
  top: 5.5rem;
  align-self: start;
  height: calc(100vh - 5.5rem);
  overflow-y: auto;
  padding: 1.5rem;
  border-right: 1px solid #dce2dc;
  color: #17221d;
  background: #fbfcfa;
}
.sidebar-heading { display: flex; align-items: center; justify-content: space-between; }
.sidebar-heading h2 { margin: 0; }
.sidebar-section-heading { margin-top: 2.25rem; padding-top: 1.5rem; border-top: 1px solid #dce2dc; }
.sidebar-section-heading.plugins-only { margin-top: 0; padding-top: 0; border-top: 0; }
.sidebar-section-heading h2 { margin: 0; }
.add-page { width: 2rem; height: 2rem; border-radius: 50%; font-size: 1.25rem; }
.page-sidebar nav { display: grid; gap: 0.4rem; margin-top: 1.5rem; }
.page-link {
  display: grid;
  width: 100%;
  padding: 0.75rem;
  color: #31453b;
  background: transparent;
  text-align: left;
}
.page-link:hover, .page-link.active { color: #1f4937; background: #e7eee9; }
.page-link small { margin-top: 0.2rem; color: #7c8982; font-weight: 500; }
.plugin-link small { font-size: .62rem; letter-spacing: .05em; text-transform: uppercase; }
.sidebar-empty { margin-top: 2rem; color: #758078; font-size: 0.85rem; line-height: 1.5; }
.page-workspace { width: min(100%, 62rem); padding: 2rem clamp(1rem, 4vw, 4rem) 5rem; }
.page-workspace.plugin-workspace { width: 100%; max-width: none; padding-inline: clamp(1rem, 2.5vw, 3rem); }
.editor-loading { padding: 4rem; color: #68776f; text-align: center; }
.editor-message { padding: 0.85rem 1rem; border-radius: 0.65rem; }
.error-message { color: #86251f; background: #fee9e7; }
.success-message { color: #295b40; background: #e2f1e7; }
.info-message { color: #4c596e; background: #e8edf7; }
.page-form { display: grid; gap: 1.25rem; margin: 0; }
.form-card { display: grid; gap: 1.25rem; padding: 1.5rem; border: 1px solid #dce2dc; border-radius: 1rem; background: white; }
.section-heading { justify-content: space-between; gap: 1rem; }
.section-heading h2 { margin: 0; }
.two-columns { display: grid; gap: 1rem; grid-template-columns: 1fr 1fr; }
textarea {
  width: 100%;
  resize: vertical;
  border: 1px solid #cbd5ce;
  border-radius: 0.65rem;
  padding: 0.8rem;
  color: #17221d;
  background: #fbfcfa;
  font: inherit;
  line-height: 1.55;
}
textarea:focus { border-color: #285c47; outline: 3px solid rgb(40 92 71 / 15%); }
.sections { display: grid; gap: 1rem; }
.content-section { display: grid; gap: 1rem; padding: 1.25rem; border: 1px solid #e0e5e1; border-radius: 0.8rem; background: #fafbf9; }
.content-section-heading { justify-content: space-between; color: #536259; font-size: 0.78rem; font-weight: 800; text-transform: uppercase; }
.content-section-heading > div { display: flex; gap: 0.35rem; }
.mini-button { min-width: 1.8rem; height: 1.8rem; padding-inline: 0.45rem; font-size: 0.7rem; }
.mini-button.danger { color: #94372f; background: #fae9e7; }
.empty-sections { padding: 2rem; border: 1px dashed #bdc8c0; border-radius: 0.8rem; text-align: center; }
.form-footer { justify-content: space-between; gap: 2rem; color: #6d7972; font-size: 0.82rem; }
button:disabled, input:disabled, textarea:disabled { cursor: not-allowed; opacity: 0.62; }
@media (max-width: 850px) {
  .editor-topbar { position: static; align-items: flex-start; gap: 1rem; }
  .topbar-actions { flex-wrap: wrap; justify-content: flex-end; }
  .editor-layout { grid-template-columns: 1fr; }
  .page-sidebar { position: static; height: auto; overflow: visible; border-right: 0; border-bottom: 1px solid #dce2dc; }
  .page-sidebar nav { display: flex; overflow-x: auto; }
  .page-link { min-width: 10rem; }
}
@media (max-width: 600px) {
  .editor-topbar { padding: 1rem; flex-direction: column; }
  .topbar-actions { width: 100%; justify-content: flex-start; }
  .two-columns { grid-template-columns: 1fr; }
  .form-footer { align-items: stretch; flex-direction: column; }
}
</style>
